// App Configuration
export const APP_NAME = import.meta.env.VITE_APP_NAME || '股数通';
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.0.1';

// Feature Flags
// 控制是否允许邮箱认证（登录/注册/找回密码）
// 当前仅支持手机号认证，邮箱功能暂时禁用
export const ALLOW_EMAIL_AUTH = false;
// Web Backend API URL - Empty string for local dev (uses Vite proxy), or set VITE_API_BASE_URL for production
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
// Stock Data Server API URL - For workbench stock queries
const rawStockApiBaseUrl = import.meta.env.VITE_STOCK_API_URL || '';
const normalizedStockApiBaseUrl = rawStockApiBaseUrl.replace(/\/+$/, '');
const stockApiHasStockPrefix = (() => {
  if (!normalizedStockApiBaseUrl) return false;
  try {
    const { pathname } = new URL(normalizedStockApiBaseUrl);
    return pathname.replace(/\/+$/, '') === '/stock';
  } catch {
    return normalizedStockApiBaseUrl.endsWith('/stock');
  }
})();
// Accept both `https://domain` (direct FastAPI) and `https://domain/stock` (nginx prefix).
export const STOCK_API_BASE_URL = normalizedStockApiBaseUrl;
// Documentation URL - Supports environment variable override
export const DOCS_URL =
  import.meta.env.VITE_DOCS_URL ||
  (import.meta.env.DEV ? 'http://localhost:5174/docs' : '/docs');

// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints (future implementation)
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    REFRESH: '/api/auth/refresh',
    VERIFY_EMAIL: '/api/auth/verify-email',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    ME: '/api/auth/me',
  },

  // User endpoints (web_backend)
  USERS: {
    ME: '/api/users/me',
    PROFILE: '/api/users/profile',
    UPDATE_USERNAME: '/api/users/me/username',
    PREFERENCES: '/api/users/me/preferences',
    DELETE_ACCOUNT: '/api/users/me/delete',
  },

  // API Keys endpoints (web_backend)
  API_KEYS: {
    LIST: '/api/keys',
    CREATE: '/api/keys',
    GET: (id: string) => `/api/keys/${id}`,
    DELETE: (id: string) => `/api/keys/${id}`,
    REGENERATE: (id: string) => `/api/keys/${id}/regenerate`,
  },

  // Feedback endpoints (web_backend)
  FEEDBACK: {
    LIST: '/api/feedback',
    GET: (id: string) => `/api/feedback/${id}`,
    CREATE: '/api/feedback',
    DELETE: (id: string) => `/api/feedback/${id}`,
    ADD_COMMENT: (id: string) => `/api/feedback/${id}/comments`,
    DELETE_COMMENT: (feedbackId: string, commentId: number) =>
      `/api/feedback/${feedbackId}/comments/${commentId}`,
    VOTE_COMMENT: (feedbackId: string, commentId: number) =>
      `/api/feedback/${feedbackId}/comments/${commentId}/vote`,
    UPVOTE: (id: string) => `/api/feedback/${id}/upvote`,
    UPDATE_STATUS: (id: string) => `/api/feedback/${id}/status`,
  },

  // Usage endpoints
  USAGE: {
    RECORDS: '/api/usage/records',
    DAILY_STATS: '/api/usage/daily-stats',
    RECORD_DETAIL: (id: string) => `/api/usage/records/${id}`,
  },

  // Subscription endpoints (future implementation)
  SUBSCRIPTION: {
    CURRENT: '/api/subscription',
    PLANS: '/api/subscription/plans',
    SUBSCRIBE: '/api/subscription/subscribe',
    CANCEL: '/api/subscription/cancel',
    UPDATE: '/api/subscription/update',
  },

  // Stock data endpoints (for workbench - @server/)
  // 路径相对于 STOCK_API_BASE_URL (http://domain or http://domain/stock)
  STOCK: stockApiHasStockPrefix
    ? {
        QUERY: '/query',
        SEARCH: '/search',
      }
    : {
        QUERY: '/api/v1/stock/query',
        SEARCH: '/api/v1/stock/search',
      },
};

// Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  THEME: 'theme',
  LANGUAGE: 'language',
};

// Pricing Plans
export const PRICING_PLANS = [
  {
    tier: 'free' as const,
    name: '免费版',
    price: 0,
    monthly_quota: 1000,
    features: [
      '每月 1000 次调用',
      '基础 API 访问',
      '社区支持',
      '标准响应速度',
    ],
  },
  {
    tier: 'basic' as const,
    name: '基础版',
    price: 9.9,
    monthly_quota: 3000,
    features: [
      '每月 3000 次调用',
      '完整 API 访问',
      '优先支持',
      '更快响应速度',
      '使用分析报告',
    ],
    popular: true,
  },
  {
    tier: 'premium' as const,
    name: '高级版',
    price: 19.9,
    monthly_quota: 8000,
    features: [
      '每月 8000 次调用',
      '完整 API 访问',
      '7x24 专属支持',
      '最快响应速度',
      '高级分析报告',
      '自定义配置',
    ],
  },
];

// MCP Access Methods
export const MCP_ACCESS_METHODS = [
  {
    id: 'sse',
    name: 'SSE (Server-Sent Events)',
    description: '服务器推送事件，实时流式响应',
    icon: 'Radio',
    configExample: `{
  "mcpServers": {
    "fetch-stock-data": {
      "url": "https://mcp.example.com/mcp-sse?key=YOUR_API_KEY"
    }
  }
}`,
  },
  {
    id: 'streamable-http',
    name: 'StreamableHTTP',
    description: '可流式传输的 HTTP 连接',
    icon: 'Waves',
    configExample: `{
  "mcpServers": {
    "fetch-stock-data": {
      "type": "streamable_http",
      "url": "https://api.example.com/mcp-streamable?key=YOUR_API_KEY"
    }
  }
}`,
  },
  {
    id: 'http',
    name: 'HTTP REST API',
    description: '标准 HTTP RESTful API',
    icon: 'Globe',
    configExample: `curl -X POST https://api.example.com/stock/query \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "查询腾讯股票最近一周的数据"}'`,
  },
  {
    id: 'uvx',
    name: 'uvx CLI',
    description: 'Python uvx 命令行工具',
    icon: 'Terminal',
    configExample: `{
  "mcpServers": {
    "fetch-stock-data": {
      "command": "uvx",
      "args": ["fetch-stock-data"],
      "env": {
        "FETCH_STOCK_API_KEY": "YOUR_API_KEY"
      }
    }
  }
}`,
  },
  {
    id: 'npx',
    name: 'npx CLI',
    description: 'Node.js npx 命令行工具',
    icon: 'Box',
    configExample: `{
  "mcpServers": {
    "fetch-stock-data": {
      "command": "npx",
      "args": ["-y", "fetch-stock-data@latest"],
      "env": {
        "FETCH_STOCK_API_KEY": "YOUR_API_KEY"
      }
    }
  }
}`,
  },
];

// Validation Rules
export const VALIDATION_RULES = {
  EMAIL: {
    PATTERN: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    MIN_LENGTH: 5,
    MAX_LENGTH: 100,
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 50,
    PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  },
  API_KEY_NAME: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 50,
    PATTERN: /^[a-zA-Z0-9_-]+$/,
  },
};

// Toast Configuration
export const TOAST_CONFIG = {
  DURATION: {
    SUCCESS: 3000,
    ERROR: 5000,
    WARNING: 4000,
    INFO: 3000,
  },
  POSITION: 'top-right' as const,
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'yyyy-MM-dd HH:mm:ss',
  API: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  DATE_ONLY: 'yyyy-MM-dd',
  TIME_ONLY: 'HH:mm:ss',
};

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// Request Timeout
export const REQUEST_TIMEOUT = {
  DEFAULT: 30000, // 30 seconds
  UPLOAD: 120000, // 2 minutes
  DOWNLOAD: 60000, // 1 minute
};
